import React, { createContext, useContext, useState, useEffect } from 'react';

const AccessibilityContext = createContext();

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};

export const AccessibilityProvider = ({ children }) => {
  // Load saved preferences
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('accessibilitySettings');
    return saved ? JSON.parse(saved) : {
      enabled: false,
      fontSize: 'normal', // normal, large, xl
      contrast: 'normal', // normal, high
      reducedMotion: false,
      screenReaderMode: false,
    };
  });

  // Save preferences
  useEffect(() => {
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
    
    // Apply classes to html element
    const html = document.documentElement;
    
    // Clear all accessibility classes first
    html.classList.remove(
      'a11y-enabled', 
      'a11y-font-large', 
      'a11y-font-xl',
      'a11y-high-contrast',
      'a11y-reduced-motion',
      'a11y-screen-reader'
    );
    
    if (settings.enabled) {
      html.classList.add('a11y-enabled');
      
      if (settings.fontSize === 'large') {
        html.classList.add('a11y-font-large');
      } else if (settings.fontSize === 'xl') {
        html.classList.add('a11y-font-xl');
      }
      
      if (settings.contrast === 'high') {
        html.classList.add('a11y-high-contrast');
      }
      
      if (settings.reducedMotion) {
        html.classList.add('a11y-reduced-motion');
      }
      
      if (settings.screenReaderMode) {
        html.classList.add('a11y-screen-reader');
      }
    }
  }, [settings]);

  // Announce changes to screen readers
  const announce = (message) => {
    const announcement = document.getElementById('a11y-announcer');
    if (announcement) {
      announcement.textContent = message;
      setTimeout(() => {
        announcement.textContent = '';
      }, 1000);
    }
  };

  const toggleAccessibility = () => {
    const newEnabled = !settings.enabled;
    setSettings(prev => ({ ...prev, enabled: newEnabled }));
    announce(newEnabled ? 'Accessibility mode enabled' : 'Accessibility mode disabled');
  };

  const setFontSize = (size) => {
    setSettings(prev => ({ ...prev, fontSize: size }));
    announce(`Font size set to ${size}`);
  };

  const setContrast = (level) => {
    setSettings(prev => ({ ...prev, contrast: level }));
    announce(`Contrast set to ${level}`);
  };

  const toggleReducedMotion = () => {
    const newValue = !settings.reducedMotion;
    setSettings(prev => ({ ...prev, reducedMotion: newValue }));
    announce(newValue ? 'Animations reduced' : 'Animations enabled');
  };

  const toggleScreenReaderMode = () => {
    const newValue = !settings.screenReaderMode;
    setSettings(prev => ({ ...prev, screenReaderMode: newValue }));
    announce(newValue ? 'Screen reader optimizations enabled' : 'Screen reader optimizations disabled');
  };

  return (
    <AccessibilityContext.Provider value={{ 
      settings,
      toggleAccessibility,
      setFontSize,
      setContrast,
      toggleReducedMotion,
      toggleScreenReaderMode,
      announce
    }}>
      {/* Screen reader announcer */}
      <div 
        id="a11y-announcer"
        role="status" 
        aria-live="polite" 
        aria-atomic="true"
        className="sr-only"
      />
      {/* Skip to main content link */}
      <a 
        href="#main-content" 
        className="skip-link"
        onClick={(e) => {
          e.preventDefault();
          const main = document.getElementById('main-content');
          if (main) {
            main.focus();
            main.scrollIntoView();
          }
        }}
      >
        Skip to main content
      </a>
      {children}
    </AccessibilityContext.Provider>
  );
};

export default AccessibilityContext;
